import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, FolderTree, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SectionCard } from './shared/SectionCard';
import { FormField } from './shared/FormField';
import { NativeSelect } from './shared/NativeSelect';
import { SearchInput } from './shared/SearchInput';
import { RegistrarPagoModal } from './shared/RegistrarPagoModal';
import { ArbolEgresosPendientes } from './egresos/ArbolEgresosPendientes';
import { useArbolEgresos } from '../hooks/useArbolEgresos';
import { useCajas } from '../hooks/useCajas';
import { useAlert } from '@/hooks/useAlert';
import { useAuthorization } from '@/auth/hooks/useAuthorization';
import { SECTION_LABEL_CLASS } from '../constants/ui';
import { CONTABILIDAD_ROUTES } from '../constants/routes';
import { formatAmount } from '../utils/formatters';
import { MONEDAS, filtrarArbol } from '../utils/agrupadores';
import type { EgresoPendiente, Moneda, NodoEgresos, PagoPayload } from '../types';

const DEFAULTS = { moneda: 'todas' as Moneda | 'todas', search: '' };

export function EgresosPendientesPage() {
  const { successAlert, errorAlert } = useAlert();
  const { arbol, isLoading, pagarAgrupador, pagarItem, isPagando } = useArbolEgresos();
  const { cajas } = useCajas();
  const { canCreateContabilidad } = useAuthorization();

  const [moneda, setMoneda] = useState(DEFAULTS.moneda);
  const [search, setSearch] = useState(DEFAULTS.search);
  const [pagandoNodo, setPagandoNodo] = useState<{ nodo: NodoEgresos; moneda: Moneda } | null>(null);
  const [pagandoItem, setPagandoItem] = useState<EgresoPendiente | null>(null);

  const handleLimpiar = () => {
    setMoneda(DEFAULTS.moneda);
    setSearch(DEFAULTS.search);
  };

  // "Sin agrupar" va al final, como un nodo más.
  const nodos = useMemo(
    () => (arbol ? filtrarArbol([...arbol.agrupadores, arbol.sinAgrupar], { moneda, search }) : []),
    [arbol, moneda, search],
  );

  const saldoTotal = useMemo(() => {
    const raices = arbol ? [...arbol.agrupadores, arbol.sinAgrupar] : [];
    return MONEDAS.map((m) => ({ moneda: m, saldo: raices.reduce((acc, n) => acc + n.totales[m].saldo, 0) }))
      .filter((t) => t.saldo > 0 && (moneda === 'todas' || t.moneda === moneda));
  }, [arbol, moneda]);

  async function handleSubmitPagoNodo(payload: PagoPayload) {
    if (!pagandoNodo || pagandoNodo.nodo.id === null) return;
    try {
      const { imputaciones } = await pagarAgrupador({ id: pagandoNodo.nodo.id, moneda: pagandoNodo.moneda, payload });
      successAlert({
        title: 'Pago registrado',
        message: `${pagandoNodo.nodo.nombre}: se imputó a ${imputaciones.length} ${imputaciones.length === 1 ? 'ítem' : 'ítems'}, del más viejo al más nuevo.`,
      });
      setPagandoNodo(null);
    } catch (err) {
      errorAlert({ title: 'Error al registrar pago', exception: err });
    }
  }

  async function handleSubmitPagoItem(payload: PagoPayload) {
    if (!pagandoItem) return;
    try {
      await pagarItem({ id: pagandoItem.id, payload });
      successAlert({ title: 'Pago registrado', message: `${pagandoItem.itemNombre} actualizado correctamente.` });
      setPagandoItem(null);
    } catch (err) {
      errorAlert({ title: 'Error al registrar pago', exception: err });
    }
  }

  if (isLoading) {
    return <div className="p-5 text-sm text-muted-foreground">Cargando...</div>;
  }

  const totalesNodo = pagandoNodo ? pagandoNodo.nodo.totales[pagandoNodo.moneda] : null;

  return (
    <>
      <div className="space-y-5 p-2 sm:p-5">

        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-bold text-gray-900">Egresos Pendientes</h1>
          <Button asChild variant="outline" size="sm" className="gap-1.5 rounded-full">
            <Link to={CONTABILIDAD_ROUTES.AGRUPADORES}>
              <FolderTree className="size-4" /> Agrupadores
            </Link>
          </Button>
        </div>

        <SectionCard className="overflow-hidden">

          <div className="flex flex-wrap items-center gap-2 px-5 py-4 border-b border-gray-100">
            <AlertCircle className="size-4 text-muted-foreground" />
            <p className={SECTION_LABEL_CLASS}>Gestión de Egresos Pendientes</p>
            {saldoTotal.length > 0 && (
              <p className="ml-auto text-sm text-muted-foreground">
                Saldo total:{' '}
                {saldoTotal.map((t, i) => (
                  <span key={t.moneda} className="font-semibold tabular-nums text-gray-900">
                    {i > 0 && ' · '}{t.moneda} $ {formatAmount(t.saldo, t.moneda)}
                  </span>
                ))}
              </p>
            )}
          </div>

          <div className="px-5 py-4 border-b border-gray-100">
            <div className="rounded-xl border border-gray-200 p-4">
              <div className="flex gap-3 items-end flex-wrap">
                <FormField label="Moneda" className="w-36">
                  <NativeSelect value={moneda} onChange={(e) => setMoneda(e.target.value as Moneda | 'todas')}>
                    <option value="todas">Todas</option>
                    <option value="ARS">ARS</option>
                    <option value="USD">USD</option>
                  </NativeSelect>
                </FormField>
                <FormField label="Buscar" className="flex-1 min-w-48">
                  <SearchInput
                    placeholder="Buscar agrupador, ítem, cliente o trámite..."
                    value={search}
                    onChange={setSearch}
                  />
                </FormField>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLimpiar}
                  className="h-9 gap-1.5 text-muted-foreground hover:text-foreground shrink-0"
                >
                  <X className="size-3.5" /> Limpiar
                </Button>
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                Pagar un agrupador incluye sus subagrupadores y salda los ítems del más viejo al más nuevo.
              </p>
            </div>
          </div>

          <ArbolEgresosPendientes
            nodos={nodos}
            moneda={moneda}
            expandirTodo={search.trim() !== ''}
            canPagar={canCreateContabilidad}
            disabled={isPagando}
            onPagarNodo={(nodo, m) => setPagandoNodo({ nodo, moneda: m })}
            onPagarItem={setPagandoItem}
          />

        </SectionCard>
      </div>

      {pagandoNodo && totalesNodo && (
        <RegistrarPagoModal
          open={true}
          titulo="Pagar agrupador"
          cajaLabel="Caja Origen"
          itemNombre={pagandoNodo.nodo.nombre}
          clienteNombre={`${totalesNodo.cantidad} ${totalesNodo.cantidad === 1 ? 'ítem pendiente' : 'ítems pendientes'} · se salda del más viejo al más nuevo`}
          total={totalesNodo.total}
          totalMovido={totalesNodo.pagado}
          saldoRestante={totalesNodo.saldo}
          moneda={pagandoNodo.moneda}
          cajas={cajas}
          isSubmitting={isPagando}
          onSubmit={handleSubmitPagoNodo}
          onClose={() => setPagandoNodo(null)}
        />
      )}

      {pagandoItem && (
        <RegistrarPagoModal
          open={true}
          titulo="Registrar Pago"
          permitirExcedente
          cajaLabel="Caja Origen"
          itemNombre={pagandoItem.itemNombre}
          clienteNombre={pagandoItem.clienteNombre}
          total={pagandoItem.total}
          totalMovido={pagandoItem.totalPagado}
          saldoRestante={pagandoItem.saldoRestante}
          moneda={pagandoItem.moneda}
          cajas={cajas}
          isSubmitting={isPagando}
          onSubmit={handleSubmitPagoItem}
          onClose={() => setPagandoItem(null)}
        />
      )}
    </>
  );
}
